/**
 * TodoSurface — Phase 6.C
 *
 * Lists every TODO/FIXME/XXX/HACK marker scanned out of the kitchen
 * codebases by console-reporter. Sortable, filterable, click → opens
 * the file in VS Code via the `vscode://file/` URL scheme.
 *
 * Source: R2:_console/todos.json (rebuilt every 5 min by
 * `_sync_todos()`).
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { TodosIndex, TodoItem } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

type MarkerFilter = "ALL" | "TODO" | "FIXME" | "XXX" | "HACK";

interface ViewState {
  search: string;
  marker: MarkerFilter;
  repo:   string;          // "ALL" or specific repo
}
const view: ViewState = {
  search: "",
  marker: "ALL",
  repo:   "ALL",
};

const REPO_ROOTS: Record<string, string> = {
  "sports-betting-research": "/Users/yurysankov/Code/sports-betting-research",
  "dashboard-site":           "/Users/yurysankov/Code/sports-betting-research/dashboard-site",
  "the-kitchen-console":      "/Users/yurysankov/Code/the-kitchen-console",
};

const MARKER_COLORS: Record<string, string> = {
  TODO:  "var(--accent-blue-light)",
  FIXME: "var(--accent-red-light)",
  XXX:   "var(--accent-amber)",
  HACK:  "var(--accent-purple-light)",
};

export function renderTodoSurface(onChange: () => void): TemplateResult {
  const e = store.get<TodosIndex>("todos");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const idx = e.data;
  if ((idx as any).error === "todos_not_synced") return notSyncedView();

  const filtered = applyFilters(idx.todos);
  const allRepos = ["ALL", ...Object.keys(idx.by_repo)];

  return html`
    <div class="todos">
      ${headerCard(idx, filtered, allRepos, onChange)}
      ${idx.total === 0
        ? cleanCodebaseView()
        : tableSection(filtered, onChange)}
    </div>
  `;
}

// =============================================================
// Header
// =============================================================
function headerCard(
  idx: TodosIndex,
  filtered: TodoItem[],
  repos: string[],
  onChange: () => void,
): TemplateResult {
  const markers: MarkerFilter[] = ["ALL", "TODO", "FIXME", "XXX", "HACK"];
  return html`
    <section class="panel todos-header">
      <div class="todos-header__row">
        <div class="todos-header__metric">
          <span class="todos-header__label">total</span>
          <span class="todos-header__value">${idx.total}</span>
          <span class="todos-header__sub">${filtered.length} match filters</span>
        </div>
        <div class="todos-header__metric">
          <span class="todos-header__label">by marker</span>
          <span class="todos-header__breakdown">
            ${Object.entries(idx.by_marker).map(([m, n]) => html`
              <span class="todos-header__pill" style="color:${MARKER_COLORS[m] ?? "var(--text-tertiary)"};">
                ${m} ${n}
              </span>
            `)}
            ${Object.keys(idx.by_marker).length === 0 ? html`
              <span style="color:var(--text-quaternary);font-size:11px;">none</span>
            ` : ""}
          </span>
        </div>

        <span class="todos-header__spacer"></span>

        <input
          class="todos-header__search"
          type="search"
          placeholder="Search file, text…"
          .value=${view.search}
          @input=${(e: Event) => {
            view.search = (e.target as HTMLInputElement).value;
            onChange();
          }}
        />

        <div class="todos-header__chips">
          ${markers.map((m) => html`
            <button
              class="todos-chip ${view.marker === m ? "is-active" : ""}"
              @click=${() => { view.marker = m; onChange(); }}
            >${m}</button>
          `)}
        </div>

        <select class="todos-header__repo"
                @change=${(e: Event) => {
                  view.repo = (e.target as HTMLSelectElement).value;
                  onChange();
                }}>
          ${repos.map((r) => html`
            <option value=${r} ?selected=${r === view.repo}>
              ${r}${idx.by_repo[r] ? ` (${idx.by_repo[r]})` : ""}
            </option>
          `)}
        </select>

        ${renderExplainButton({
          label: "Explain TODOs",
          context: () => buildExplainContext(idx, filtered),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Table
// =============================================================
function tableSection(filtered: TodoItem[], _onChange: () => void): TemplateResult {
  return html`
    <section class="panel todos-list">
      ${filtered.length === 0 ? html`
        <div class="todos-list__empty">No TODOs match the current filters.</div>
      ` : html`
        <ol class="todos-list__items">
          ${filtered.map(todoRow)}
        </ol>
      `}
    </section>
  `;
}

function todoRow(t: TodoItem): TemplateResult {
  const color = MARKER_COLORS[t.marker] ?? "var(--text-secondary)";
  const repoRoot = REPO_ROOTS[t.repo];
  const vscodeHref = repoRoot
    ? `vscode://file/${repoRoot}/${t.path}:${t.line}`
    : null;
  const mtimeShort = t.mtime
    ? new Date(t.mtime).toLocaleDateString([], {
        month: "short", day: "numeric",
      })
    : "—";

  return html`
    <li class="todo-row">
      <span class="todo-row__marker" style="color:${color}; border-color:${color};">
        ${t.marker}
      </span>
      <span class="todo-row__text">${t.text}</span>
      <span class="todo-row__path">
        ${vscodeHref ? html`
          <a href=${vscodeHref}>
            <code>${t.repo}/${t.path}</code>:<strong>${t.line}</strong>
          </a>
        ` : html`
          <code>${t.repo}/${t.path}</code>:<strong>${t.line}</strong>
        `}
      </span>
      <span class="todo-row__mtime">${mtimeShort}</span>
    </li>
  `;
}

// =============================================================
// State views
// =============================================================
function cleanCodebaseView(): TemplateResult {
  return html`
    <section class="panel todos-clean">
      <div class="todos-clean__icon">✓</div>
      <h3>Codebase is clean</h3>
      <p>No <code>TODO:</code>, <code>FIXME:</code>, <code>XXX:</code>, or <code>HACK:</code> markers found across the three kitchen repos.</p>
      <p class="todos-clean__sub">
        Scanner runs every 5 min via console-reporter. The regex requires a
        colon after the marker, so prose like "Note that…" or "TODO scanner"
        doesn't trigger false positives.
      </p>
    </section>
  `;
}

function notSyncedView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        <p style="font-size:15px;color:var(--text-primary);margin:0 0 8px;">
          TODO scanner hasn't run yet
        </p>
        <p style="margin:0;">
          The reporter scans every 5 minutes. Wait for the next :00 / :05 / :10
          UTC tick or trigger manually:
        </p>
        <pre style="margin:14px auto 0;text-align:left;
                     font-size:11px;background:var(--bg-glass-thin);
                     border:1px solid var(--border-1);
                     border-radius:8px;padding:10px;
                     max-width:480px;color:var(--accent-blue-light);">
~/.hermes/hermes-agent/venv/bin/python3 \\
  ~/Code/sports-betting-research/notebooks/console_reporter.py
        </pre>
      </div>
    </div>
  `;
}

function skeletonView(): TemplateResult {
  return html`
    <div class="todos">
      <section class="panel" style="height:80px;"></section>
      <section class="panel" style="min-height:480px;padding:24px;">
        ${Array.from({ length: 6 }, () => html`
          <div class="skeleton" style="height:18px;margin-bottom:8px;"></div>
        `)}
      </section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`<div class="panel" style="padding:48px;text-align:center;color:var(--text-tertiary);">No TODO data.</div>`;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>TODOs</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}

// =============================================================
// Filtering
// =============================================================
function applyFilters(todos: TodoItem[]): TodoItem[] {
  let out = todos;
  if (view.marker !== "ALL") out = out.filter((t) => t.marker === view.marker);
  if (view.repo !== "ALL")   out = out.filter((t) => t.repo === view.repo);
  if (view.search.trim()) {
    const q = view.search.trim().toLowerCase();
    out = out.filter((t) =>
      [t.path, t.text, t.repo].some((s) => s.toLowerCase().includes(q)),
    );
  }
  return out;
}

function buildExplainContext(idx: TodosIndex, filtered: TodoItem[]): string {
  const lines = [
    `TODO inventory:`,
    `Total: ${idx.total} markers across ${Object.keys(idx.by_repo).length} repos`,
    `By marker: ${JSON.stringify(idx.by_marker)}`,
    `By repo: ${JSON.stringify(idx.by_repo)}`,
    `Currently filtered to: ${filtered.length}`,
  ];
  if (filtered.length > 0) {
    lines.push("", "Sample (first 10):");
    for (const t of filtered.slice(0, 10)) {
      lines.push(`  [${t.marker}] ${t.repo}/${t.path}:${t.line}: ${t.text}`);
    }
  }
  return lines.join("\n");
}
